import { buildLayerPanelHoverFactPreview } from "./map-hover-facts.js";
import { mapText } from "./map-i18n.js";
import { buildLayerSettingsView } from "./map-layer-settings.js";
import {
  clampLayerOpacity,
  clampPointIconScale,
  flattenLayerClipMasks,
  isFixedGroundLayer,
  isLayerClipMaskSource,
  resolveLayerEntries,
} from "./map-layer-state.js";

function escapeAttribute(value) {
  return String(value || "").replace(/"/g, "&quot;");
}

const LAYER_ICON_BY_ID = Object.freeze({
  bookmarks: "bookmarks",
  fish_evidence: "fish-fill",
  hotspots: "bird-fill",
  live_events: "wifi-fill",
  minimap: "mountain-2-fill",
  node_waypoints: "map-pin",
  region_groups: "hover-resources",
  region_nodes: "share-nodes",
  regions: "trade-origin",
  trade_npcs: "wheel-fill",
  zone_mask: "hover-zone",
});

function layerIconName(layer) {
  const layerId = String(layer?.layerId || "").trim();
  if (LAYER_ICON_BY_ID[layerId]) {
    return LAYER_ICON_BY_ID[layerId];
  }
  if (layer?.kind === "waypoints") {
    return "map-pin";
  }
  if (layer?.kind === "tiled-raster") {
    return "map-view";
  }
  if (layer?.kind === "field" || layer?.kind === "vector-geojson") {
    return "hover-zone";
  }
  return "squares-2x2";
}

function layerIconMarkup(layer) {
  return `<svg class="fishy-icon fishymap-layer-type-icon size-4" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${escapeAttribute(layerIconName(layer))}"></use></svg>`;
}

export function renderLayerStack(container, stateBundle, options = {}) {
  const layers = resolveLayerEntries(stateBundle);
  const expandedLayerIds =
    options.expandedLayerIds instanceof Set ? options.expandedLayerIds : new Set();
  const renderLoadingPanelMarkup =
    typeof options.renderLoadingPanelMarkup === "function"
      ? options.renderLoadingPanelMarkup
      : (label) => String(label || "");
  const escapeHtml =
    typeof options.escapeHtml === "function" ? options.escapeHtml : (value) => String(value || "");
  const eyeIcon = typeof options.eyeIcon === "function" ? options.eyeIcon : () => "";
  const selection =
    options.selection && typeof options.selection === "object" ? options.selection : null;
  const hover = options.hover && typeof options.hover === "object" ? options.hover : null;
  const zoneCatalog = Array.isArray(options.zoneCatalog) ? options.zoneCatalog : [];
  const hoverFactVisibilityByLayer =
    options.hoverFactVisibilityByLayer && typeof options.hoverFactVisibilityByLayer === "object"
      ? options.hoverFactVisibilityByLayer
      : {};
  const sampleHoverVisibleByLayer =
    options.sampleHoverVisibleByLayer && typeof options.sampleHoverVisibleByLayer === "object"
      ? options.sampleHoverVisibleByLayer
      : {};
  const lineagePanelVisibleByLayer =
    options.lineagePanelVisibleByLayer && typeof options.lineagePanelVisibleByLayer === "object"
      ? options.lineagePanelVisibleByLayer
      : {};
  const activeSettingsTabByLayer =
    options.activeSettingsTabByLayer instanceof Map
      ? options.activeSettingsTabByLayer
      : new Map();
  const liveEventsEligible = options.liveEventsEligible === true;
  const hoverFactPreviewByLayer = new Map(
    layers.map((layer) => [
      layer.layerId,
      buildLayerPanelHoverFactPreview({
        layerId: layer.layerId,
        hover,
        selection,
        zoneCatalog,
        visibilityByLayer: hoverFactVisibilityByLayer,
      }),
    ]),
  );
  const settingsViewByLayer = new Map(
    layers
      .filter((layer) => expandedLayerIds.has(layer.layerId))
      .map((layer) => [
        layer.layerId,
        buildLayerSettingsView({
          layer,
          hoverFactRows: hoverFactPreviewByLayer.get(layer.layerId) || [],
          sampleHoverVisible: sampleHoverVisibleByLayer[layer.layerId] !== false,
          lineagePanelVisible: lineagePanelVisibleByLayer[layer.layerId] === true,
          requestedTab: activeSettingsTabByLayer.get(layer.layerId),
          escapeHtml,
        }),
      ]),
  );

  if (!layers.length) {
    const loadingKey = "__loading__";
    if (container.dataset.renderKey !== loadingKey) {
      container.dataset.renderKey = loadingKey;
      container.innerHTML = renderLoadingPanelMarkup(mapText("layers.loading_registry"));
    }
    return;
  }

  const renderKey = JSON.stringify(
    layers.map((layer) => [
      layer.layerId,
      layer.name,
      layer.kind,
      Boolean(layer.visible),
      Math.round(clampLayerOpacity(layer.opacity) * 1000),
      Math.round(clampLayerOpacity(layer.opacityDefault) * 1000),
      layer.clipMaskLayerId || "",
      layer.supportsWaypointConnections ? 1 : 0,
      layer.waypointConnectionsVisible ? 1 : 0,
      layer.supportsWaypointLabels ? 1 : 0,
      layer.waypointLabelsVisible ? 1 : 0,
      layer.supportsPointIcons ? 1 : 0,
      layer.pointIconsVisible ? 1 : 0,
      layer.supportsPointClustering ? 1 : 0,
      layer.pointClusteringEnabled ? 1 : 0,
      layer.supportsPointIcons && sampleHoverVisibleByLayer[layer.layerId] !== false ? 1 : 0,
      layer.supportsCoordinateLineage && lineagePanelVisibleByLayer[layer.layerId] === true ? 1 : 0,
      Math.round(clampPointIconScale(layer.pointIconScale) * 1000),
      Math.round(clampPointIconScale(layer.pointIconScaleDefault) * 1000),
      Number.isFinite(layer.displayOrder) ? layer.displayOrder : 0,
      layer.locked ? 1 : 0,
      layer.layerId === "live_events" && !liveEventsEligible ? 1 : 0,
      expandedLayerIds.has(layer.layerId) ? 1 : 0,
      expandedLayerIds.has(layer.layerId)
        ? settingsViewByLayer.get(layer.layerId)?.renderKey || []
        : [],
    ]),
  );
  if (container.dataset.renderKey === renderKey) {
    return;
  }
  container.dataset.renderKey = renderKey;

  const layerById = new Map(layers.map((layer) => [layer.layerId, layer]));
  const layerNameById = new Map(layers.map((layer) => [layer.layerId, layer.name]));
  const clipMasks = {};
  for (const layer of layers) {
    const clipMaskLayerId = String(layer.clipMaskLayerId || "").trim();
    if (
      !clipMaskLayerId ||
      clipMaskLayerId === layer.layerId ||
      !layerNameById.has(clipMaskLayerId) ||
      !isLayerClipMaskSource(layerById.get(clipMaskLayerId)) ||
      isFixedGroundLayer(clipMaskLayerId)
    ) {
      continue;
    }
    clipMasks[layer.layerId] = clipMaskLayerId;
  }
  const flatClipMasks = flattenLayerClipMasks(clipMasks);
  const clippedLayersByMask = new Map();
  for (const layer of layers) {
    const clipMaskLayerId = String(flatClipMasks[layer.layerId] || "").trim();
    if (!clipMaskLayerId) {
      continue;
    }
    const clippedLayers = clippedLayersByMask.get(clipMaskLayerId) || [];
    clippedLayers.push({
      layer,
      indentLevel: 1,
    });
    clippedLayersByMask.set(clipMaskLayerId, clippedLayers);
  }
  const displayedLayers = [];
  const displayedLayerIds = new Set();
  for (const layer of layers) {
    if (flatClipMasks[layer.layerId]) {
      continue;
    }
    displayedLayers.push({ layer, indentLevel: 0 });
    displayedLayerIds.add(layer.layerId);
    for (const child of clippedLayersByMask.get(layer.layerId) || []) {
      displayedLayers.push(child);
      displayedLayerIds.add(child.layer.layerId);
    }
  }
  for (const layer of layers) {
    if (displayedLayerIds.has(layer.layerId)) {
      continue;
    }
    displayedLayers.push({ layer, indentLevel: 0 });
  }

  container.innerHTML = displayedLayers
    .map(({ layer, indentLevel }) => {
      const visible = Boolean(layer.visible);
      const locked = Boolean(layer.locked);
      const disabled = layer.layerId === "live_events" && !liveEventsEligible;
      const clipMaskSource = !locked && isLayerClipMaskSource(layer);
      const settingsExpanded = expandedLayerIds.has(layer.layerId);
      const visibilityLabel = visible ? mapText("layers.hide") : mapText("layers.show");
      const visibilityDescription = disabled
        ? mapText("layers.live_events_unavailable")
        : `${visibilityLabel} ${layer.name}`;
      const settingsLabel = settingsExpanded
        ? mapText("layers.settings_hide", { name: layer.name })
        : mapText("layers.settings_show", { name: layer.name });
      const settingsView = settingsViewByLayer.get(layer.layerId);
      return `
        <article
          class="fishymap-layer-card card card-border bg-base-200"
          data-layer-id="${escapeAttribute(layer.layerId)}"
          data-indent-level="${indentLevel > 0 ? "1" : "0"}"
          data-locked="${locked ? "true" : "false"}"
          data-disabled="${disabled ? "true" : "false"}"
          data-settings-expanded="${settingsExpanded ? "true" : "false"}"
          data-clip-mask-source="${clipMaskSource ? "true" : "false"}"
          style="--fishymap-layer-indent:${indentLevel};"
        >
          <div
            class="fishymap-layer-summary"
            data-layer-settings-toggle="${escapeAttribute(layer.layerId)}"
            data-layer-drag="${escapeAttribute(layer.layerId)}"
            role="button"
            tabindex="0"
            aria-label="${escapeHtml(settingsLabel)}"
            aria-expanded="${settingsExpanded ? "true" : "false"}"
            data-fishy-tooltip="${escapeHtml(settingsLabel)}"
            data-fishy-tooltip-mode="anchor-only"
            draggable="${locked ? "false" : "true"}"
          >
            <div class="fishymap-layer-header">
              ${layerIconMarkup(layer)}
              <span class="fishymap-layer-title-text">${escapeHtml(layer.name)}</span>
            </div>
            <button
              class="fishymap-layer-visibility btn btn-sm btn-circle ${
                visible ? "btn-soft btn-primary" : "btn-ghost"
              }"
              data-layer-visibility="${escapeAttribute(layer.layerId)}"
              data-layer-visible="${visible ? "true" : "false"}"
              type="button"
              aria-label="${escapeHtml(visibilityDescription)}"
              data-fishy-tooltip="${escapeHtml(visibilityDescription)}"
              data-fishy-tooltip-mode="anchor-only"
              draggable="false"
              ${disabled ? "disabled" : ""}
            >
              ${eyeIcon(visible)}
            </button>
          </div>
          ${
            settingsExpanded
              ? `
                <div class="fishymap-layer-body min-w-0">
                  ${settingsView?.markup || ""}
                </div>
              `
              : ""
          }
        </article>
      `;
    })
    .join("");
}
